import {
  CustomFeature,
  CustomLatLng,
  LatLong,
  TipoMapaTalhoes,
} from "core/features/maps/typings";
import { DEFAULT_ZOOM, options } from "./config";
import { GoogleMap } from "@react-google-maps/api";
import {
  ReactChild,
  ReactChildren,
  useEffect,
  useMemo,
  useState,
} from "react";

import MapControl from "./components/controls";
import { MapTypeId } from "core/features/maps/enums";
import { CurrentViewTypeEnum, PointsBaseMap } from "./typings";
import ProductivityAndArea from "./components/productivityAndArea";
import { containerStyle } from "./styles";
import { useMap } from "./hooks/useMap";
import { GeometryType } from "./typings";
import { useFilters } from "core/features/filters/store";
import RainfallInterpolationLayer from "./components/RainfallInterpolationLayer";
import {
  TalhaoCodigoMarker,
  getTalhaoMarkerKey,
  isEmptyFeatureId,
} from "./components/TalhaoCodigoMarker";
import { ChuvasStationMarker } from "./components/ChuvasStationMarker";
import {
  useViewportStationKeys,
  useViewportTalhaoIds,
} from "./hooks/useViewportTalhaoIds";

import * as S from "./styles";
import "./pluviometrico.css";

interface BaseMapProps {
  children?: ReactChild | ReactChildren | null;
  center?: LatLong;
  points?: PointsBaseMap;
  talhoes?: string[];
  precipitacao?: string;
  isLoading?: boolean;
  onLoadMap?: (map: google.maps.Map | undefined) => void;
  onUnmountMap?: (map: google.maps.Map | undefined) => void;
  onClickItem?: (latLng: CustomLatLng, feature: CustomFeature) => void;
}

function BaseMap({
  children,
  center,
  points,
  talhoes,
  precipitacao,
  isLoading,
  onLoadMap,
  onUnmountMap,
  onClickItem,
}: BaseMapProps) {
  const {
    onLoad,
    onUnmount,
    mapRef,
    displayMarkers,
    zoomAllowsMarkers,
    isLoaded,
  } = useMap({
    onClickItem,
    points,
    center,
    talhoes,
  });

  const {
    filters: { tipoMapa },
  } = useFilters();

  const url = window.location.href;
  const isPrecipitacaoPage = url.includes("precipitacao");
  const isChuvasMap = Number(tipoMapa) === TipoMapaTalhoes.Chuvas;

  const [currentViewType, setCurrentViewType] = useState<CurrentViewTypeEnum>(
    isPrecipitacaoPage || isChuvasMap
      ? CurrentViewTypeEnum.numberOnly
      : CurrentViewTypeEnum.box
  );

  useEffect(() => {
    if (isChuvasMap) {
      setCurrentViewType(CurrentViewTypeEnum.numberOnly);
    }
  }, [isChuvasMap]);

  const Polygons = useMemo(
    () =>
      points?.features.filter(
        (feature) =>
          feature.geometry.type === GeometryType.Polygon ||
          feature.geometry.type === GeometryType.MultiPolygon
      ) || [],
    [points]
  );

  const stationItems = useMemo(() => {
    const features =
      points?.features.filter(
        (feature) => feature.geometry.type === GeometryType.Point
      ) || [];

    return features.map((feature, index) => {
      const coordinates =
        feature.geometry.type === GeometryType.Point
          ? feature.geometry.coordinates
          : [0, 0];
      const rawId = feature.properties?.id ?? feature.id;
      // Always include coords + index: API often repeats station ids (e.g. 03112DC9).
      const key = `station:${rawId ?? "no-id"}:${coordinates[0]}:${coordinates[1]}:${index}`;
      return {
        key,
        lat: coordinates[1],
        lng: coordinates[0],
        precipitacao: feature.properties?.precipitacao,
      };
    });
  }, [points]);

  // Same as Mapa de Execução: only mount markers in the padded viewport (on idle).
  const visibleTalhaoIds = useViewportTalhaoIds(
    mapRef,
    Polygons,
    zoomAllowsMarkers
  );

  const visibleStationKeys = useViewportStationKeys(
    mapRef,
    stationItems,
    zoomAllowsMarkers
  );

  const visiblePolygons = useMemo(() => {
    if (!visibleTalhaoIds) return Polygons;
    return Polygons.filter((item) => {
      const id = item.properties?.id;
      const codigo = item.properties?.codigo;
      const candidates = [
        !isEmptyFeatureId(id) ? String(id) : null,
        codigo != null ? String(codigo) : null,
      ].filter(Boolean) as string[];
      return candidates.some((candidate) => visibleTalhaoIds.has(candidate));
    });
  }, [Polygons, visibleTalhaoIds]);

  const visibleStations = useMemo(() => {
    if (!visibleStationKeys) return stationItems;
    return stationItems.filter((station) =>
      visibleStationKeys.has(station.key)
    );
  }, [stationItems, visibleStationKeys]);

  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={DEFAULT_ZOOM}
        onLoad={(map: google.maps.Map) => {
          onLoad(map);
          onLoadMap?.(map);
        }}
        onUnmount={(map: google.maps.Map) => {
          onUnmount();
          onUnmountMap?.(map);
        }}
        mapTypeId={MapTypeId.HYBRID}
        options={{ ...options }}
      >
        {children}
        {isChuvasMap && isLoaded ? (
          <RainfallInterpolationLayer map={mapRef.current} points={points} />
        ) : null}
        <S.MarkersContainer>
          {(zoomAllowsMarkers ? visiblePolygons : []).map((item, index) => (
            <TalhaoCodigoMarker
              key={getTalhaoMarkerKey(item, index)}
              item={item}
              currentViewType={currentViewType}
              displayMarkers={displayMarkers}
              onClickItem={onClickItem}
            />
          ))}

          {(zoomAllowsMarkers ? visibleStations : []).map((station, index) => (
            <ChuvasStationMarker
              key={station.key || `station-fallback:${index}`}
              markerKey={station.key}
              lat={station.lat}
              lng={station.lng}
              precipitacao={station.precipitacao}
              visible={displayMarkers}
              isChuvasStyle={isChuvasMap}
              precipitacaoFallback={precipitacao}
            />
          ))}
        </S.MarkersContainer>
      </GoogleMap>

      {mapRef.current ? (
        <MapControl
          center={center!}
          map={mapRef.current}
          currentViewType={currentViewType}
          handleChangeCurrentViewType={setCurrentViewType}
          points={points}
          enableRainfallPngExport={isChuvasMap || isPrecipitacaoPage}
        />
      ) : null}

      <ProductivityAndArea
        Properties={points?.Properties}
        isLoading={isLoading as boolean}
      />
    </>
  );
}

export default BaseMap;
